import React, { useState } from 'react';
import { cn, Icon } from '@lqa-frontend/ui-system';
import { BodyM, BodyS, BodySSemiBold } from '../typography';
import { Button } from '../buttons/Button';
import { DropdownMenu } from '../dropdown';
import type { ITablePaginationProps } from './types';

const DEFAULT_ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function TablePagination({
	currentPage,
	totalItems,
	itemsPerPage,
	onPageChange,
	onItemsPerPageChange,
	itemsPerPageOptions = DEFAULT_ITEMS_PER_PAGE_OPTIONS,
	labelShowing = 'Showing',
	labelItemsPerPage = 'Items per page',
	className,
	shouldHidePerPage,
	shouldHideSummary,
	isDisabled = false,
}: ITablePaginationProps) {
	const [itemsPerPageOpen, setItemsPerPageOpen] = useState<boolean>(false);
	const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
	const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
	const endItem = Math.min(currentPage * itemsPerPage, totalItems);

	const handlePrev = () => {
		if (currentPage > 1) onPageChange(currentPage - 1);
	};

	const handleNext = () => {
		if (currentPage < totalPages) onPageChange(currentPage + 1);
	};

	const getPageNumbers = (): (number | 'ellipsis')[] => {
		if (totalPages <= 6) {
			return Array.from({ length: totalPages }, (_, index) => index + 1);
		}
		if (currentPage <= 3) {
			return [1, 2, 3, 4, 'ellipsis', totalPages];
		}
		if (currentPage >= totalPages - 2) {
			return [
				1,
				'ellipsis',
				totalPages - 3,
				totalPages - 2,
				totalPages - 1,
				totalPages,
			];
		}
		return [
			1,
			'ellipsis',
			currentPage - 1,
			currentPage,
			currentPage + 1,
			'ellipsis',
			totalPages,
		];
	};

	const pageNumbers = getPageNumbers();

	return (
		<div
			className={cn(
				'flex flex-wrap items-center justify-between gap-12 px-10 py-10 border-t border-divider',
				className
			)}
			aria-disabled={isDisabled || undefined}
			data-slot="table-pagination"
			data-testid="table-pagination"
		>
			<div className="flex items-center gap-8">
				{!shouldHidePerPage && (
					<>
						<BodyS color="secondary" className="shrink-0">
							{labelItemsPerPage}
						</BodyS>
						{onItemsPerPageChange && (
							<DropdownMenu
								open={itemsPerPageOpen}
								onOpenChange={setItemsPerPageOpen}
							>
								<DropdownMenu.Trigger asChild>
									<Button variant="neutral" size="xs" disabled={isDisabled}>
										<BodySSemiBold color="secondary">
											{itemsPerPage}
										</BodySSemiBold>
										{itemsPerPageOpen ? (
											<Icon
												name="solidArrowUp"
												className="fill-zinc-300 [&_path]:fill-zinc-300"
											/>
										) : (
											<Icon
												name="solidArrowDown"
												className="fill-zinc-300 [&_path]:fill-zinc-300"
											/>
										)}
									</Button>
								</DropdownMenu.Trigger>
								<DropdownMenu.Content
									align="start"
									onCloseAutoFocus={(event) => {
										event.preventDefault();
									}}
								>
									{itemsPerPageOptions.map((opt) => (
										<DropdownMenu.Item
											key={opt}
											onSelect={() => onItemsPerPageChange(opt)}
										>
											{opt}
										</DropdownMenu.Item>
									))}
								</DropdownMenu.Content>
							</DropdownMenu>
						)}
						{!onItemsPerPageChange && (
							<BodyM color="secondary">{itemsPerPage}</BodyM>
						)}
					</>
				)}
			</div>

			<div className="flex items-center gap-8">
				<Button
					variant="neutral"
					type="button"
					onClick={handlePrev}
					disabled={isDisabled || currentPage <= 1}
					className="flex min-h-23 min-w-25 items-center justify-center rounded-s p-0"
					aria-label="Previous page"
					data-testid="table-pagination-prev"
				>
					<Icon name="arrowLeft" />
				</Button>
				{pageNumbers.map((page, index) =>
					page === 'ellipsis' ? (
						<BodyM key={`ellipsis-${index}`} color="secondary">
							…
						</BodyM>
					) : (
						<Button
							key={page}
							variant="neutral"
							type="button"
							onClick={() => onPageChange(page)}
							disabled={isDisabled}
							className={cn(
								'min-w-25 min-h-23 w-25 h-23 rounded-s text-12 flex items-center justify-center',
								currentPage === page &&
									'border border-brand-blue-700 bg-white text-brand-blue-700 font-semibold'
							)}
							data-testid={`table-pagination-page-${page}`}
						>
							{page}
						</Button>
					)
				)}
				<Button
					variant="neutral"
					type="button"
					onClick={handleNext}
					disabled={isDisabled || currentPage >= totalPages}
					className="flex min-h-23 min-w-25 items-center justify-center rounded-s p-0"
					aria-label="Next page"
					data-testid="table-pagination-next"
				>
					<Icon name="arrowRight" />
				</Button>
			</div>

			<BodyS
				color="secondary"
				className="shrink-0"
				data-testid="table-pagination-summary"
			>
				{!shouldHideSummary && (
					<>
						{labelShowing} {totalItems === 0 ? '0' : `${startItem}–${endItem}`}{' '}
						of {totalItems}
					</>
				)}
			</BodyS>
		</div>
	);
}

export { TablePagination };
